import React, { useState } from 'react';

import colors from '~/assets/colors.module.scss';
import type { AiChatReference } from '~/components/AiChat/types';
import { CliveAvatar } from '~/components/Clive/CliveAvatar';
import { CliveCopyToClipboardButton } from '~/components/Clive/CliveCopyToClipboardButton';
import CliveMarkdownDisplay from '~/components/Clive/CliveMarkdownDisplay/CliveMarkdownDisplay';
import { FsIconButton, Text } from '~/components/core';
import CircularProgress from '~/components/core/Atomic/CircularProgress/CircularProgress';
import Skeleton from '~/components/core/Atomic/Skeleton';
import NewRefreshIcon from '~/components/icons/NewRefreshIcon';
import { safeMixpanelTrack } from '~/components/SystemConfiguration/Tabs/PermissionsProfiles/MixpanelEvents';
import { serverDateTimeToLocal } from '~/DateTimeUtils';
import { CLIVE_MIXPANEL_EVENTS } from '~/pocs/mixpanel';
import type { ClaimAISummaryResponse } from '~/server_shared/openapi/data-contracts';
import cn from '~/Utils/cn';

import type { SummaryProcessingState } from './hooks/useClaimAISummary';
import { useClaimAISummary } from './hooks/useClaimAISummary';

const MAX_COLLAPSED_LENGTH = 500;

const ClaimOverviewCardSkeleton: React.FC = () => (
  <>
    <Skeleton variant="text" width={200} height={28} />
    <div className="flex flex-col gap-8">
      <Skeleton variant="text" width="75%" height={20} />
      <Skeleton variant="text" width="100%" height={20} />
      <Skeleton variant="text" width="66%" height={20} />
    </div>
  </>
);

interface ShowMoreContentProps {
  content: string;
  references?: Record<string, AiChatReference>;
  onShowMoreClick?: () => void;
}

const ShowMoreContent: React.FC<ShowMoreContentProps> = ({ content, references, onShowMoreClick }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const shouldTruncate = content.length > MAX_COLLAPSED_LENGTH;
  const displayContent = shouldTruncate && !isExpanded ? content.slice(0, MAX_COLLAPSED_LENGTH) + '...' : content;

  const handleToggle = () => {
    if (!isExpanded && onShowMoreClick) {
      onShowMoreClick();
    }
    setIsExpanded(!isExpanded);
  };
  return (
    <div>
      <div className="text-md">
        <CliveMarkdownDisplay content={displayContent} references={references} />
      </div>
      {shouldTruncate && (
        <div className="mt-20 flex cursor-pointer justify-center" onClick={handleToggle}>
          <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.MEDIUM} className="text-aiV2-pink hover:opacity-80">
            {isExpanded ? 'Show Less' : 'Show More'}
          </Text>
        </div>
      )}
    </div>
  );
};

interface CardParts {
  isLoading: boolean;
  isProcessing: boolean;
  subtitle?: string;
  contentText?: string;
}

const getCardParts = (processingState: SummaryProcessingState, summary: ClaimAISummaryResponse | null): CardParts => {
  const summaryText = summary?.summary_text;

  switch (processingState) {
    case 'loading':
      return { isLoading: true, isProcessing: false };

    case 'processing':
      if (summaryText) {
        return {
          isLoading: false,
          isProcessing: true,
          subtitle:
            "You're viewing the latest summary. New information is being processed and may update the summary once ready.",
          contentText: summaryText,
        };
      }
      return {
        isLoading: false,
        isProcessing: true,
        subtitle:
          'Clive is still reviewing the initial claim details and may update this summary as processing completes.',
      };

    case 'success':
      return {
        isLoading: false,
        isProcessing: false,
        contentText: summaryText || '',
      };

    case 'failed':
      if (summaryText) {
        // needs_refresh is true for every failed row, so it cannot say whether the stored text is
        // also out of date -- is_content_stale is the flag that can, and the reader needs to know.
        return {
          isLoading: false,
          isProcessing: false,
          subtitle: summary?.is_content_stale
            ? "You're viewing the last generated summary, which may be out of date. The most recent update didn't complete — use refresh to try again."
            : "You're viewing the last generated summary. The most recent update didn't complete — use refresh to try again.",
          contentText: summaryText,
        };
      }
      return {
        isLoading: false,
        isProcessing: false,
        contentText: 'Unable to generate claim summary. Please try again later.',
      };

    case 'timeout':
      return {
        isLoading: false,
        isProcessing: false,
        contentText: 'Summary generation is taking longer than expected. Please check back later.',
      };

    default:
      return { isLoading: false, isProcessing: false };
  }
};

export interface ClaimOverviewCardViewProps {
  summary: ClaimAISummaryResponse | null;
  processingState: SummaryProcessingState;
  onRefresh: () => void;
  isRefreshing?: boolean;
}

export const ClaimOverviewCardView: React.FC<ClaimOverviewCardViewProps> = ({
  summary,
  processingState,
  onRefresh,
  isRefreshing,
}) => {
  const { isLoading, isProcessing, subtitle, contentText } = getCardParts(processingState, summary);
  const hasSummaryText = Boolean(summary?.summary_text);
  const avatarMood = isProcessing ? CliveAvatar.MOOD.LOADING : CliveAvatar.MOOD.HAPPY;

  const handleCopyClick = () => {
    safeMixpanelTrack(CLIVE_MIXPANEL_EVENTS.CLAIM_OVERVIEW_COPY_CLICKED);
  };

  const handleShowMoreClick = () => {
    safeMixpanelTrack(CLIVE_MIXPANEL_EVENTS.CLAIM_OVERVIEW_SHOW_MORE_CLICKED);
  };

  const handleRefreshClick = () => {
    safeMixpanelTrack(CLIVE_MIXPANEL_EVENTS.CLAIM_OVERVIEW_REFRESH_CLICKED);
    onRefresh();
  };
  const renderTitleRow = () => (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-8">
        <Text weight={Text.WEIGHTS.SEMI_BOLD} variant={Text.VARIANTS.XL} colorVariant={Text.COLOR_VARIANTS.PRIMARY}>
          Claim Overview
        </Text>
        {!isProcessing &&
          (isRefreshing ? (
            <CircularProgress size={20} style={{ color: colors.textPrimary }} />
          ) : (
            <FsIconButton onClick={handleRefreshClick} aria-label="Refresh summary">
              <NewRefreshIcon size={20} iconColor={colors.textPrimary} />
            </FsIconButton>
          ))}
        {summary?.summary_content_updated_at && (
          <Text variant={Text.VARIANTS.XS} weight={Text.WEIGHTS.REGULAR} colorVariant={Text.COLOR_VARIANTS.PRIMARY}>
            Last updated {serverDateTimeToLocal(summary.summary_content_updated_at)}
          </Text>
        )}
      </div>
      {hasSummaryText && (
        <CliveCopyToClipboardButton
          textToCopy={summary!.summary_text!}
          ariaLabel="Copy claim overview"
          onCopy={handleCopyClick}
        />
      )}
    </div>
  );

  const renderContent = () => (
    <>
      {renderTitleRow()}
      {subtitle && (
        <Text
          weight={Text.WEIGHTS.SEMI_BOLD}
          variant={Text.VARIANTS.SM}
          colorVariant={Text.COLOR_VARIANTS.PRIMARY}
          className={contentText ? 'mb-8' : undefined}
        >
          {subtitle}
        </Text>
      )}
      {contentText && (
        <ShowMoreContent content={contentText} references={summary?.references} onShowMoreClick={handleShowMoreClick} />
      )}
    </>
  );

  return (
    <div className={cn('flex min-h-[200px] w-full gap-12 rounded-lg border border-slate-200 bg-white p-24 shadow-sm')}>
      <div className="flex-shrink-0">
        <CliveAvatar mood={avatarMood} size={44} />
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-12">
        {isLoading ? <ClaimOverviewCardSkeleton /> : renderContent()}
      </div>
    </div>
  );
};

export const ClaimOverviewCard: React.FC = () => {
  const { summary, processingState, triggerRefresh, isRefreshing } = useClaimAISummary();

  return (
    <ClaimOverviewCardView
      summary={summary}
      processingState={processingState}
      onRefresh={triggerRefresh}
      isRefreshing={isRefreshing}
    />
  );
};

export default ClaimOverviewCard;
